import React from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  StickerSun, StickerPalm, StickerSurf, StickerShoe, StickerCassette,
  StickerSmile, StickerHeart, StickerStar, StickerBolt, StickerPeace,
  StickerWave, StickerFlower, StickerSkate, StickerVan,
  StickerMoon, StickerSunflower, StickerCampfire, StickerTent, StickerTree,
  StickerSwirl, StickerYinWave, StickerHibiscus, StickerMeltSmile,
  StickerCheckerWave, StickerEye,
} from "../components/Stickers";
import Guestbook from "../components/Guestbook";

const SectionTitle = ({ children, color = "#F87171" }) => (
  <h2 className="font-display tracking-wider text-5xl sm:text-6xl uppercase leading-none"
      style={{ color, textShadow: "4px 4px 0 rgba(0,0,0,0.18)" }}>
    {children}
  </h2>
);

const Polaroid = ({ src, rotate = 0, tape = true, alt = "" }) => (
  <div className="relative polaroid" style={{ transform: `rotate(${rotate}deg)` }}>
    {tape && <div className="tape" style={{ top: -10, left: "50%", transform: "translateX(-50%) rotate(-3deg)" }} />}
    <div className="aspect-square overflow-hidden border-2 border-black bg-[#FFF4D6]">
      <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" />
    </div>
  </div>
);

const Home = () => {
  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="sunset-bg relative overflow-hidden border-b-4 border-black scanlines">
        <div className="absolute inset-0 dotgrid opacity-20 pointer-events-none" />

        {/* scattered stickers — beachy mix, kept clear of text */}
        <div className="absolute top-6 left-6 float-slow hover-wobble" style={{ "--rot": "-12deg" }}><StickerSun size={90} rotate={-12} /></div>
        <div className="absolute top-10 right-8 float-med hover-wobble" style={{ "--rot": "14deg" }}><StickerPalm size={110} rotate={14} /></div>
        <div className="absolute bottom-6 left-6 float-slow hover-wobble" style={{ "--rot": "-8deg" }}><StickerVan size={130} rotate={-8} /></div>
        <div className="absolute bottom-8 right-6 float-med hover-wobble" style={{ "--rot": "8deg" }}><StickerSurf size={120} rotate={8} /></div>
        <div className="hidden lg:block absolute top-[18%] right-[20%] float-slow hover-wobble" style={{ "--rot": "10deg" }}><StickerSwirl size={80} rotate={10} /></div>
        <div className="hidden lg:block absolute top-[55%] right-[4%] float-med hover-wobble" style={{ "--rot": "-12deg" }}><StickerYinWave size={75} rotate={-12} /></div>

        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32 relative z-10">
          <div className="font-pixel text-xl uppercase tracking-widest text-black/70 mb-4">
            ▓▓▓ Welcome to my corner of the internet ▓▓▓
          </div>
          <h1 className="font-display tracking-wider uppercase text-7xl sm:text-8xl lg:text-[10rem] leading-[0.85] text-black">
            Hi, I'm <span className="text-[#F87171]">Jax</span>.<br/>
            <span className="text-[#2A9D8F]">I run</span> for <br/>
            people who <span className="text-[#B57FA0]">can't</span>.
          </h1>
          <p className="font-body text-xl sm:text-2xl mt-8 max-w-3xl text-black/85 leading-relaxed">
            28 years old. Pembroke-born, Winnipeg-raised. Father, adventurer, storyteller.
            I ran <strong>7,000 km east to west, from St. John's, Newfoundland to Port Coquitlam, BC</strong>,
            finishing at Terry Fox's resting place. Slept in a <strong>1985 VW Vanagon</strong>, livestreamed
            every step, and raised <strong>$100,000+ for cancer research</strong> along the road.
            None of it happened alone. The community that showed up, watched, donated, and shared
            my story is the real engine behind every number on this site.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/accomplishments" data-testid="hero-cta-story"
              className="font-display tracking-wider text-2xl uppercase px-7 py-3 bg-[#F87171] text-white border-4 border-black shadow-[8px_8px_0_#000] btn-press">
              The Whole Story →
            </Link>
            <Link to="/speaking" data-testid="hero-cta-book"
              className="font-display tracking-wider text-2xl uppercase px-7 py-3 bg-[#F4D58D] text-black border-4 border-black shadow-[8px_8px_0_#000] btn-press">
              Book Me to Speak
            </Link>
            <Link to="/shop" data-testid="hero-cta-shop"
              className="font-display tracking-wider text-2xl uppercase px-7 py-3 bg-white text-black border-4 border-black shadow-[8px_8px_0_#000] btn-press">
              Shop <span className="blink ml-2 text-[#F87171]">(soon)</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ STATS MARQUEE ============ */}
      <section className="bg-black text-[#F4D58D] border-b-4 border-black py-4">
        <Marquee gradient={false} speed={70} direction="right">
          <span className="font-display text-4xl tracking-wider px-10">7,000 KM EAST TO WEST</span><span className="text-[#F87171] font-display text-4xl px-3">★</span>
          <span className="font-display text-4xl tracking-wider px-10">16TH HUMAN ACROSS CANADA ON FOOT</span><span className="text-[#8ECAE6] font-display text-4xl px-3">★</span>
          <span className="font-display text-4xl tracking-wider px-10">$100,000+ RAISED FOR CANCER RESEARCH</span><span className="text-[#F4A261] font-display text-4xl px-3">★</span>
          <span className="font-display text-4xl tracking-wider px-10">280 KM PEI · 65 HRS · NO SLEEP</span><span className="text-[#B57FA0] font-display text-4xl px-3">★</span>
          <span className="font-display text-4xl tracking-wider px-10">1,000 BOOKS SOLD</span><span className="text-[#F4D58D] font-display text-4xl px-3">★</span>
          <span className="font-display text-4xl tracking-wider px-10">5,000+ STUDENTS INSPIRED</span><span className="text-[#F87171] font-display text-4xl px-3">★</span>
        </Marquee>
      </section>

      {/* ============ MY STORY ============ */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute top-1/2 right-2 hidden lg:block" style={{ "--rot": "10deg" }}><StickerHibiscus size={80} rotate={10} className="float-slow" /></div>
        <div className="absolute bottom-20 right-4 hidden xl:block" style={{ "--rot": "-8deg" }}><StickerMeltSmile size={90} rotate={-8} className="float-med" /></div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-5 gap-12 items-start">
          <div className="md:col-span-3">
            <SectionTitle color="#F87171">My Story</SectionTitle>
            <div className="font-body text-lg sm:text-xl mt-8 space-y-5 leading-relaxed text-black/85">
              <p>
                I'm Jackson Charron-Okerlund. Most people just call me <strong>Jax</strong>. Born in Pembroke, Ontario,
                raised in Winnipeg, and still calling it home.
              </p>
              <p>
                Before any of the running, I worked in therapeutic recreation with older adults. I spent my days with
                people in their 90s and 100s, watching whole generations of family fill a room on a birthday. It taught
                me what a long life actually looks like, and how much further some people deserve to get.
              </p>
              <p>
                Terry Fox was one of the first names I ever knew. Growing up Canadian, his story is the floor everything
                else gets built on. So in 2023 I laced up in <strong>St. John's, Newfoundland</strong> and ran west, 7,000
                kilometres across the country, finishing at <strong>Terry's resting place in Port Coquitlam, BC</strong>.
              </p>
              <p>
                For the <strong>first 40 days</strong> I pushed a baby stroller with my tent and gear and slept wherever
                the road let me. After that, a <strong>1985 VW Vanagon</strong> became home for the rest of the run.
                The whole journey was livestreamed on <strong>TikTok</strong>, shared along the way on
                <strong> Instagram and Facebook</strong>, and picked up by local and national news. Strangers turned into
                a community, donating, sharing, and meeting me on highway shoulders. They're the reason that number kept climbing.
              </p>
              <p>
                <strong className="bg-[#F4D58D] px-2 border-2 border-black">7,000 km · 160 days · $100,000+ for cancer research.</strong>
                I'm the 16th human to ever cross Canada on foot.
              </p>
              <p>
                After the run, I wrote a book about it. Self-published, picked up the printed copies in person, packed
                a minivan, and drove across the country a second time. <strong>50 cities in 65 days</strong>, selling
                <strong> 1,000 books</strong> and raising another $6,070 for cancer patients. Every signed copy moved
                because someone in that town showed up.
              </p>
              <p>
                After the book tour, I became a public speaker for the <strong>Terry Fox Foundation</strong>. The
                <strong> 2024 New Brunswick ambassador</strong>, and today a <strong>national guest speaker</strong>.
                Over 5,000 students have heard the story in person so far.
              </p>
              <p>
                Also after the book tour, I ran the length of Prince Edward Island.
                <strong> 280 km in 65 hours, no sleep</strong>, becoming only the second human on record to do it.
              </p>
              <p>
                My son was born <strong>July 17, 2024</strong>. He's the reason the next chapter looks the way it does.
              </p>
            </div>
          </div>

          {/* Polaroids */}
          <div className="md:col-span-2 grid grid-cols-2 gap-6 relative">
            <div className="absolute -top-8 -right-4 z-20 hidden md:block" style={{ "--rot": "20deg" }}><StickerFlower size={70} rotate={20} className="float-slow" /></div>
            <Polaroid rotate={-4} src="/home/jc_1.jpg" alt="Jax 1" />
            <Polaroid rotate={3}  src="/home/jc_2.jpg" alt="Jax 2" />
            <Polaroid rotate={2}  src="/home/1985_westy_1.jpg" alt="The 1985 Westy" />
            <Polaroid rotate={-5} src="/home/1983_westy_2.jpg" alt="The 1983 Westy" />
          </div>
        </div>
      </section>

      {/* ============ HIGHLIGHTS GRID ============ */}
      <section className="bg-[#0A0A0A] text-white py-24 px-6 border-y-4 border-black relative overflow-hidden">
        <div className="absolute top-8 right-8 hidden md:block"><StickerBolt size={70} rotate={12} className="float-med" /></div>
        <div className="max-w-7xl mx-auto">
          <SectionTitle color="#F4D58D">Highlights</SectionTitle>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {[
              { n: "7,000", l: "kilometres run · St. John's to Port Coquitlam", bg: "#F87171" },
              { n: "16th", l: "human ever to cross Canada on foot", bg: "#B57FA0" },
              { n: "$100K+", l: "raised for cancer research", bg: "#8ECAE6" },
              { n: "280", l: "km across PEI · no sleep · 65 hrs", bg: "#F4A261" },
              { n: "1,000", l: "books sold on the cross-Canada tour", bg: "#F4D58D", dark: true },
              { n: "5,000+", l: "students inspired in person", bg: "#2A9D8F" },
            ].map((s) => (
              <div key={s.l} data-testid={`stat-${s.n}`}
                className="bg-white text-black border-4 border-black shadow-[10px_10px_0_#F4D58D] p-7 hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[6px_6px_0_#F4D58D] transition-all">
                <div className="inline-block px-4 py-1 mb-4 border-4 border-black font-display text-5xl"
                  style={{ background: s.bg, color: s.dark ? "#000" : "#fff" }}>
                  {s.n}
                </div>
                <div className="font-pixel text-2xl">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/accomplishments" data-testid="home-see-all-accomplishments"
              className="inline-block font-display tracking-wider text-2xl px-8 py-3 bg-[#F87171] border-4 border-white text-white shadow-[6px_6px_0_#F4D58D] btn-press">
              See everything →
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FAMILY + VAN ============ */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute top-6 left-4 hidden md:block"><StickerSmile size={80} rotate={-10} className="float-slow" /></div>
        <div className="absolute bottom-6 right-4 hidden md:block"><StickerSwirl size={90} rotate={6} className="float-med" /></div>
        <div className="absolute top-1/2 left-2 hidden xl:block"><StickerEye size={100} rotate={-8} className="float-slow" /></div>
        <div className="absolute top-1/3 right-2 hidden xl:block"><StickerYinWave size={80} rotate={14} className="float-med" /></div>

        <div className="max-w-6xl mx-auto">
          <SectionTitle color="#B57FA0">The Crew</SectionTitle>
          <p className="font-body text-xl mt-6 max-w-3xl text-black/80">
            None of the big stuff happens without the crew! My son, my partner, and the '83 VW Vanagon.
            Come along for the ride!
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white border-4 border-black shadow-[8px_8px_0_#000] p-6 relative">
              <div className="absolute -top-6 -right-6"><StickerHeart size={60} rotate={20} /></div>
              <div className="font-display text-3xl tracking-wider">Easton</div>
              <div className="font-pixel text-xl mt-2">my son · b. July 17, 2024</div>
              <p className="mt-4 font-body text-base">Full-time dad first. He's the reason every future plan has a different shape, and a better one.</p>
            </div>
            <div className="bg-white border-4 border-black shadow-[8px_8px_0_#000] p-6 relative">
              <div className="absolute -top-6 -right-6"><StickerFlower size={60} rotate={-12} /></div>
              <div className="font-display text-3xl tracking-wider">Carly</div>
              <div className="font-pixel text-xl mt-2">partner & teammate</div>
              <p className="mt-4 font-body text-base">My beautiful, radiant earth goddess. I love her so much.</p>
            </div>
            <div className="bg-white border-4 border-black shadow-[8px_8px_0_#000] p-6 relative">
              <div className="absolute -top-6 -right-6"><StickerVan size={90} rotate={-8} /></div>
              <div className="font-display text-3xl tracking-wider">The Westy</div>
              <div className="font-pixel text-xl mt-2">'83 VW Vanagon</div>
              <p className="mt-4 font-body text-base">Old engine, big heart. Replaced my 1985 Vanagon, which burned down after the cross-Canada run.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ GUESTBOOK TEASER ============ */}
      <Guestbook />

      {/* ============ CTA STRIP ============ */}
      <section className="hazard py-3" />
      <section className="bg-[#F4D58D] py-16 px-6 border-y-4 border-black text-center relative">
        <div className="absolute top-4 left-6"><StickerStar size={60} rotate={-12} className="float-slow" /></div>
        <div className="absolute top-4 right-6"><StickerBolt size={60} rotate={14} className="float-med" /></div>

        <h3 className="font-display tracking-wider text-5xl sm:text-6xl text-black">
          Got an event? <span className="text-[#F87171]">Let's talk.</span>
        </h3>
        <p className="font-body text-xl max-w-2xl mx-auto mt-4 text-black/80">
          School assembly, fundraiser, corporate audience. I tell the real story of what it took to run across Canada,
          and what anyone in the room can do with the same engine.
        </p>
        <div className="flex flex-wrap gap-4 justify-center mt-8">
          <Link to="/speaking" data-testid="cta-book"
            className="font-display text-2xl tracking-wider px-7 py-3 bg-[#F87171] text-white border-4 border-black shadow-[8px_8px_0_#000] btn-press">
            Book a Talk
          </Link>
          <a href="mailto:CrossCanadaJax97@gmail.com" data-testid="cta-email"
            className="font-display text-2xl tracking-wider px-7 py-3 bg-white text-black border-4 border-black shadow-[8px_8px_0_#000] btn-press">
            Email Direct
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
